import { Injectable } from "@angular/core";
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { CoreStoreFacade } from "@modules/+new/core/core-store/domain/+state/core-store.facade";
import packageJson from "../../../../../../package.json";
import { TranslationService } from "@core-services/translation.service";
import { ErrorClass, OfflineError, UnauthorizedError } from "@models";
import { IGNORE_401 } from "@modules/+new/core/interceptors/http-context-tokens";
import { DestructorService } from "@core-services/destructor.service";

@Injectable()
export class GlobalInterceptorService implements HttpInterceptor {
	public constructor(
		private readonly coreStoreFacade: CoreStoreFacade,
		private readonly translationService: TranslationService,
		private readonly destructor: DestructorService
	) {}

	public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (this.isOffline()) {
			return EMPTY;
		}
		this.coreStoreFacade.increaseRequestCounter();

		const request = req.clone({
			headers: this.prepareBaseHeaders(req)
		});

		return next.handle(request).pipe(
			catchError((e: ErrorClass) => this.handleErrorAndRethrow(e, request.context.get(IGNORE_401))),
			finalize(() => this.coreStoreFacade.decreaseRequestCounter())
		);
	}

	private isOffline(): boolean {
		return this.coreStoreFacade.coreStoreSnapshot.isOffline || this.coreStoreFacade.coreStoreSnapshot.isLogout;
	}

	private prepareBaseHeaders(req: HttpRequest<unknown>): HttpHeaders {
		let lang = this.translationService.getLanguage();
		if ("" + lang === "null") {
			lang = "de";
		}

		const headersObject = {
			"access-token": this.coreStoreFacade.getAccessToken(),
			"content-type": "application/json",
			"accept-language": lang,
			"x-bitpanda-app-web": "web" + packageJson.version
		};

		if (window && window["gaId"]) {
			headersObject["X-Tracking-Id"] = String(window["gaId"]);
		}

		let headers = new HttpHeaders(headersObject);

		const headerKeys = req.headers.keys();
		headerKeys.forEach((key) => {
			headers = headers.set(key, req.headers.get(key));
		});

		return headers;
	}

	private handleErrorAndRethrow(error: ErrorClass, ignore401: boolean): Observable<never> {
		this.handleErrorSideEffects(error, ignore401);
		return throwError(error);
	}

	private handleErrorSideEffects(error: ErrorClass, ignore401: boolean): void {
		if (error instanceof OfflineError) {
			this.coreStoreFacade.updateOfflineState(true);
			return;
		}

		// TODO do we still need this "ignore401"?, it seems that it is not the best solution..
		if (!ignore401 && error instanceof UnauthorizedError) {
			this.coreStoreFacade.updateLogoutState(true);
			this.destructor.clean();
			window.location.href = "/user/login";
		}
	}
}
